/**
 * @description Dialog component for selecting and configuring nature d'assurance (Maladie, Maternité, Accident du Travail, SMG) within an FSE invoice.
 * @usageNotes Opened as a MatDialog from BsDialogComponent upon user interaction with the nature d'assurance selector.
 * Dependencies: FactureFR and DAO models (Maladie, Maternite, AccidentTravail, SMG, EmpreinteCV, EmpreinteAdr, DonneesAT), utils, constants.
 */

import { Component, inject, LOCALE_ID, OnInit, ChangeDetectionStrategy } from '@angular/core';


import { FormsModule } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

import { FactureFR, Maladie, Maternite, AccidentTravail, SMG, EmpreinteCV, EmpreinteAdr, DonneesAT } from '../shared/dao';
import * as utils from '../shared/utils';

import * as constants from '../shared/constants';

/**
 * Data payload passed into NatAssuComponent dialog.
 */
export interface DialogData {
  msg: string;
  server_url: string;
  order_id: number;
  factureFR: FactureFR;
  empreinteCVInserted: EmpreinteCV;
  empreinteADR: EmpreinteAdr;
  forceAM: boolean; // Force l'indicateur Alsace Moselle si situation AM detectée
}

@Component({
  selector: 'lib-nat-assu',
  standalone: true,
  imports: [FormsModule, MatButtonModule, MatDialogContent, MatDialogActions, MatFormFieldModule, MatInputModule, MatDatepickerModule],
  templateUrl: './nat-assu.component.html',
  styleUrls: ['../bs-dialog/bs-dialog.component.scss', './nat-assu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: LOCALE_ID, useValue: 'fr-FR' }, { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' }, { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }],
})
export class NatAssuComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<NatAssuComponent>);
  readonly dialogdata: DialogData = inject<DialogData>(MAT_DIALOG_DATA);

  /** Top accident de droit commun flag (booleans mapped to DB numeric flags) */
  public topAccidentDroitCommun: boolean = false;

  /** Top Alsace-Moselle régime local flag */
  public topAlsaceMoselle: boolean = false;

  /** Top prise en charge armateur flag (Accident du travail) */
  public topPECArmateur: boolean = false;

  /** Top présentation feuille d'accident du travail (FAT) */
  public topPresentationFAT: boolean = false;

  /** Top présence organisme AT support hors carte vitale */
  public topPresenceOrgaATSupportHorsCV: boolean = false;

  /** Reference to the Maladie sub-model */
  public maladie?: Maladie = undefined;

  /** Reference to the Maternite sub-model */
  public maternite?: Maternite = undefined;

  /** Reference to the AccidentTravail sub-model */
  public accidentTravail?: AccidentTravail = undefined;

  /** Reference to the SMG sub-model */
  public smg?: SMG = undefined;

  /** Maximum date allowed in datepickers (for maladie accident date) */
  public maxDate?: Date;

  constructor() {}

  /**
   * Initializes component state based on invoice nature d'assurance.
   */
  ngOnInit(): void {
    console.log('[NatAssuComponent.ngOnInit] this.dialogdata.factureFR.natureAssurance: ', JSON.stringify(this.dialogdata.factureFR.natureAssurance));
    switch (this.dialogdata.factureFR.natureAssurance) {
      case 'maladie':
        this.maxDate = this.dialogdata.factureFR?.dateFacture && utils.isValidDate(this.dialogdata.factureFR.dateFacture) ? utils.fromFRformat(this.dialogdata.factureFR.dateFacture) : undefined;
        this.maladie = this.dialogdata.factureFR.maladie;
        if (this.maladie) {
          this.topAccidentDroitCommun = this.maladie.topAccidentDroitCommun == 1;
          this.topAlsaceMoselle = this.dialogdata.forceAM ? true : this.maladie.topAlsaceMoselle == 1;
        }
        break;
      case 'maternite':
        this.maternite = this.dialogdata.factureFR.maternite ?? new Maternite({});
        this.maternite.dateMaternite = utils.getDateStringFromYYYYMMDD(this.maternite.dateMaternite);
        break;
      case 'accidentTravail':
        this.accidentTravail = this.dialogdata.factureFR.accidentTravail;
        if (this.accidentTravail) {
          this.topPECArmateur = this.accidentTravail.topPECArmateur == 1;
          this.topPresentationFAT = this.accidentTravail.topPresentationFAT == 1;
          this.topPresenceOrgaATSupportHorsCV = this.accidentTravail.topPresenceOrgaATSupportHorsCV == 1;
          this.accidentTravail.dateAT = utils.getDateStringFromYYYYMMDD(this.accidentTravail.dateAT);
        }
        break;
      case 'smg':
        this.smg = this.dialogdata.factureFR.smg ?? new SMG({});
        if (this.smg.totalPEC != undefined && this.smg.totalPEC != 0) {
          this.smg.totalPEC = +this.smg.totalPEC / 100;
        }
        break;
    }
  }

  /**
   * Closes the dialog, optionally saving modified nature d'assurance data.
   * @param save True to save modifications, false to cancel
   */
  public onClose(save: boolean): void {
    if (save) {
      switch (this.dialogdata.factureFR.natureAssurance) {
        case 'maladie':
          if (this.maladie) {
            this.maladie.topAccidentDroitCommun = this.topAccidentDroitCommun ? 1 : 0;
            this.maladie.topAlsaceMoselle = this.topAlsaceMoselle ? 1 : 0;
            this.maladie.dateAccidentDroitCommun = utils.tofseformat(this.maladie.dateAccidentDroitCommun ?? '');
          }
          break;
        case 'maternite':
          if (this.maternite) {
            this.maternite.dateMaternite = utils.tofseformat(this.maternite?.dateMaternite ?? '');
          }
          break;
        case 'accidentTravail':
          if (this.accidentTravail) {
            this.accidentTravail.topPECArmateur = this.topPECArmateur ? 1 : 0;
            this.accidentTravail.topPresentationFAT = this.topPresentationFAT ? 1 : 0;
            this.accidentTravail.topPresenceOrgaATSupportHorsCV = this.topPresenceOrgaATSupportHorsCV ? 1 : 0;
            this.accidentTravail.dateAT = utils.tofseformat(this.accidentTravail.dateAT ?? '');
            console.log('[onClose] accidentTravail: ', this.accidentTravail);
          }
          break;
        case 'smg':
          if (this.smg?.totalPEC != undefined) {
            this.smg.totalPEC = Math.round(+this.smg.totalPEC * 100);
          }
          break;
      }

      this.dialogRef.close({
        maladie: this.maladie,
        maternite: this.maternite,
        accidentTravail: this.accidentTravail,
        smg: this.smg,
      });
      console.log('NatAssuComponent Closing', {
        maladie: this.maladie,
        maternite: this.maternite,
        accidentTravail: this.accidentTravail,
        smg: this.smg,
      });
    } else {
      console.log('Cancelled');
      this.dialogRef.close(undefined);
    }
  }

  /**
   * Updates Accident du Travail organization and ID fields based on the selected index in AT data.
   */
  public onChangeIndexAT(): void {
    const donneesAT: DonneesAT | undefined = this.dialogdata.empreinteADR ? this.dialogdata.empreinteADR.donneesAT : this.dialogdata.empreinteCVInserted.donneesAT;
    console.log('accidentTravail?.indexAT: ', this.accidentTravail?.indexAT);
    if (this.accidentTravail?.indexAT == 1) {
      this.accidentTravail.orgaAT = '' + donneesAT?.orgaGestRisqueAT;
      this.accidentTravail.numAT = '';
    } else if (this.accidentTravail?.indexAT == 2) {
      this.accidentTravail.orgaAT = '' + donneesAT?.orgaGestAT1;
      this.accidentTravail.numAT = '' + donneesAT?.idAT1;
    } else if (this.accidentTravail?.indexAT == 3) {
      this.accidentTravail.orgaAT = '' + donneesAT?.orgaGestAT2;
      this.accidentTravail.numAT = '' + donneesAT?.idAT2;
    }
  }
}
